import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faDove,
  faHome,
  faSearch,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";
import "./Footer.css";
import { useShoppingContext } from "../context/ShoppingContext";
import FarmerMessage from "../Granja/Components/FarmerMessage/FarmerMessage"; // Import the FarmerMessage component
import { useState } from "react";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate(); // Hook to programmatically navigate
  const [loading, setLoading] = useState(true);
  const {
    aves,
    galpones,
    setShowAddAves,
    setShowAddGalpon,
    showAddAves,
    showFarmer,
    setShowFarmer,
    handleAction,
    setHandleAction,
    actionButtonText,
    alimentos,
    setActionButtonText,
    farmerMessage,
    setFarmerMessage,
    isDarkMode,
    setQuery,
    fetchMisAves,
    fetchAlimentos,
    fetchMisGalpones,
    fetchMisGranjas,
    fetchProduccionHuevos,
    setShowAddAlimento,
    isDataFetched,
    setIsDataFetched,
    controlAlimentos,
    fetchControlAlimentos,
  } = useShoppingContext();

  // PRIMERO: Realizar todos los fetches en un solo useEffect
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchProduccionHuevos(),
          fetchControlAlimentos(),
          fetchMisGranjas(),
          fetchMisGalpones(),
          fetchMisAves(),
          fetchAlimentos(),
        ]); // Marcar los datos como cargados después de todos los fetches
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
    setLoading(false);
  }, [
    setIsDataFetched,
    fetchAlimentos,
    fetchProduccionHuevos,
    fetchMisGranjas,
    fetchMisGalpones,
    fetchMisAves,
    fetchControlAlimentos,
  ]);

  // DESPUÉS: Lógica condicional para mostrar o no el mensaje
  useEffect(() => {
    if (!loading) {
      // Verificar si ya se han obtenido los datos
      if (galpones.length === 0 && aves.length === 0) {
        // Si no hay galpones ni aves, muestra el mensaje para crear un galpón
        setShowFarmer(true);
        setFarmerMessage(
          "¡Bienvenido a tu Granja! Construye el galpón donde vivirán tus siguientes aves."
        );
        setActionButtonText("Construir Galpón");
        setHandleAction(() => () => {
          setShowAddGalpon(true);
          setShowFarmer(false); // Ocultar el FarmerMessage después de la acción
        });
      } else if (galpones.length === 0) {
        // Si no hay galpones, muestra el mensaje para crear un galpón
        setShowFarmer(true);
        setFarmerMessage(
          "No tienes galpones. Por favor, agrega un galpón para continuar."
        );
        setActionButtonText("Construir Galpón");
        setHandleAction(() => () => {
          setShowAddGalpon(true);
          setShowFarmer(false); // Ocultar el FarmerMessage después de la acción
        });
      } else if (galpones.length > 0 && aves.length === 0) {
        // Si hay galpones pero no aves, muestra el mensaje para agregar aves
        setShowFarmer(true);
        setFarmerMessage(
          "¡Genial! Ahora agrega tus primeras aves para empezar a producir."
        );
        setActionButtonText("Agregar Aves");
        setHandleAction(() => () => {
          setShowAddAves(true);
          setShowFarmer(false); // Ocultar el FarmerMessage después de la acción
        });
      } else if (
        galpones.length > 0 &&
        aves.length > 0 &&
        alimentos.length === 0
      ) {
        // Si ya hay galpones y aves pero no hay alimentos, muestra el mensaje para agregar alimento.
        setShowFarmer(true);
        setFarmerMessage(
          "Has creado tus aves satisfactoriamente! Ahora crea un tipo de alimento y comienza a alimentar a tu bandada."
        );
        setActionButtonText("Agregar Alimento");
        setHandleAction(() => () => {
          setShowAddAlimento(true);
          setShowFarmer(false); // Ocultar el FarmerMessage después de la acción
        });
      } else if (controlAlimentos.length === 0) {
        // Si ya hay galpones y aves pero no hay alimentos, muestra el mensaje para agregar alimento.
        setShowFarmer(true);
        setFarmerMessage(
          "Tip: Comienza a alimentar a tus aves a diario para llevar el registro de cuánto consume cada ave y cuánto estás gastando."
        );
        setActionButtonText(`Haz click en "Alimentar aves"`);
        setHandleAction(() => () => {
          setShowFarmer(false); // Ocultar el FarmerMessage después de la acción
        });
      } else if (galpones.length > 0 && aves.length > 0) {
        // Si ya hay galpones y aves, oculta el mensaje
        setShowFarmer(false);
      }
    }
  }, [
    isDataFetched,
    galpones.length,
    aves.length,
    alimentos.length,
    controlAlimentos.length,
    loading,
    setActionButtonText,
    setShowAddAlimento,
    setShowAddAves,
    setShowAddGalpon,
    setShowFarmer,
    setFarmerMessage,
    setHandleAction,
  ]);

  // Define dark link styles
  const darkStyle = {
    color: "#aaa",
    fontWeight: "bold",
    borderRadius: "8px",
  };

  // Define light link styles
  const lightStyle = {
    color: "#333", // Darker color for light mode
    fontWeight: "bold",
    borderRadius: "8px",
  };

  // Define active link styles
  const activeStyle = {
    backgroundColor: "#1e7479", // Verde
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
  };

  // Handle click on "Mis Aves"
  const handleClickMisAves = () => {
    if (aves.length === 0 && galpones.length > 0) {
      setShowFarmer(true); // Show the farmer tutorial
      setFarmerMessage(
        "No tienes aves. Por favor, agrega algunas aves para continuar."
      );
      setActionButtonText("Agregar Aves");
      setHandleAction(() => () => {
        setShowAddAves(!showAddAves);
        setShowFarmer(false); // Ocultar el FarmerMessage después de la acción
      });
    } else if (aves.length > 0) {
      navigate("/mis-aves");
    }
  };

  // Handle click on "Mis Galpones"
  const handleClickMisGalpones = () => {
    if (galpones.length === 0) {
      setShowFarmer(true); // Show the farmer tutorial

      setFarmerMessage(
        "No tienes galpones. Por favor, agrega un galpón para continuar."
      );
      setActionButtonText("Construir Galpón");
      setHandleAction(() => () => setShowAddGalpon(true));
    } else {
      navigate("/mis-galpones");
    }
  };

  return (
    <>
      {!loading && (
        <>
          {location.pathname !== "/create" && showFarmer ? (
            <FarmerMessage
              message={farmerMessage}
              buttonText={actionButtonText}
              onClick={handleAction}
            />
          ) : (
            <></>
          )}
        </>
      )}

      <footer className={isDarkMode ? " footer-nav  dark" : "footer-nav light"}>
        <div
          className="text-decoration-none footer-link"
          onClick={() => navigate("/mis-granjas")} // Programmatically navigate to "Mis Granjas"
          style={
            location.pathname.includes("/mis-granjas")
              ? activeStyle
              : isDarkMode
              ? darkStyle
              : lightStyle
          }
        >
          <div
            className={isDarkMode ? " footer-icon  dark" : "footer-icon light"}
            style={
              location.pathname.includes("/mis-granjas")
                ? activeStyle
                : isDarkMode
                ? darkStyle
                : lightStyle
            }
          >
            <FontAwesomeIcon icon={faHome} size="lg" />
            <p className="footer-p mb-0">Granjas</p>
          </div>
        </div>

        <div
          className="text-decoration-none footer-link"
          onClick={handleClickMisAves} // Call handleClickMisAves to show tutorial if needed
          style={
            location.pathname.includes("/mis-aves")
              ? activeStyle
              : isDarkMode
              ? darkStyle
              : lightStyle
          }
        >
          <div
            className="footer-icon"
            style={
              location.pathname.includes("/mis-aves")
                ? activeStyle
                : isDarkMode
                ? darkStyle
                : lightStyle
            }
          >
            <FontAwesomeIcon icon={faDove} size="lg" />
            <p className="footer-p mb-0">Aves</p>
          </div>
        </div>

        <div
          className="text-decoration-none footer-link"
          onClick={() => navigate("/buscar")} // Programmatically navigate to "Buscar"
          style={
            location.pathname.includes("/buscar")
              ? activeStyle
              : isDarkMode
              ? darkStyle
              : lightStyle
          }
        >
          <div
            className="footer-icon"
            style={
              location.pathname.includes("/buscar")
                ? activeStyle
                : isDarkMode
                ? darkStyle
                : lightStyle
            }
            onClick={() => {
              setQuery("");
            }}
          >
            <FontAwesomeIcon
              icon={faSearch}
              size="lg"
              style={{ fontSize: 23 }}
            />
            <p className="footer-p mb-0">Buscar</p>
          </div>
        </div>

        <div
          className="text-decoration-none footer-link"
          onClick={handleClickMisGalpones} // Call handleClickMisGalpones
          style={
            location.pathname.includes("/mis-galpones")
              ? activeStyle
              : isDarkMode
              ? darkStyle
              : lightStyle
          }
        >
          <div
            className="footer-icon"
            style={
              location.pathname.includes("/mis-galpones")
                ? activeStyle
                : isDarkMode
                ? darkStyle
                : lightStyle
            }
          >
            <FontAwesomeIcon icon={faWarehouse} size="lg" />
            <p className="footer-p mb-0">Galpones</p>
          </div>
        </div>

        <div
          className="text-decoration-none footer-link"
          onClick={() => navigate("/configuracion")} // Programmatically navigate to "Configuración"
          style={
            location.pathname.includes("/configuracion")
              ? activeStyle
              : isDarkMode
              ? darkStyle
              : lightStyle
          }
        >
          <div
            className="footer-icon"
            style={
              location.pathname.includes("/configuracion")
                ? activeStyle
                : isDarkMode
                ? darkStyle
                : lightStyle
            }
          >
            <FontAwesomeIcon icon={faCog} size="lg" />
            <p className="footer-p mb-0">Configuración</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
